import { IGameplayEvent } from '../../../Core/State/IJoinedRoomState';
import { PokerBoardPresentation } from './PokerBoardPresentation';
import { PokerAudioPresentation } from './PokerAudioPresentation';
import { CommunityCardPlacement, PokerGameplayPresentation } from './PokerGameplayPresentation';
import { PokerAudioPresetManager } from './PokerAudioPresetManager';
import { WINNER_INDICATOR_DISPLAY_MS } from './PokerPresentationConfig';
import {
    createPlayerActionBadgePresentation,
    createWinnerIndicatorsPresentation,
    PlayerActionBadgePresentation,
    WinnerIndicatorsPresentation
} from './PokerPlayerPresentation';

interface RoomIdentity {
    roomId: string;
    gameId: string;
    handId?: string;
}

interface HandResetOptions {
    fadeCompletedHandPresentation?: boolean;
    playerCardsVisibleAfterClear?: boolean;
}

export interface PokerPresentationOptions {
    initialCommunityCards: readonly string[];
    getRoomId: () => string | undefined;
    getRoomIdentity: () => RoomIdentity;
    getRoomType: () => string | undefined;
    getCurrentUserId: () => string | undefined;
    audioPresetManager: PokerAudioPresetManager;
    isPlaybackAllowed: () => boolean;
    applyActionBadge: (presentation: PlayerActionBadgePresentation) => void;
    clearActionBadge: (userId: string | undefined) => void;
    clearActionBadges: () => void;
    clearNonTerminalActionBadges: () => void;
    applyWinnerIndicators: (presentation: WinnerIndicatorsPresentation) => void;
    clearWinnerIndicators: () => void;
    setVisibleCommunityCards: (cards: string[]) => void;
    clearCompletedHandPresentation: (visibleCommunityCards: string[], playerCardsVisibleAfterClear?: boolean) => void;
    setAnimatedCommunityCardDelaysMs: (delays: { [cardIndex: number]: number }) => void;
    setShowdownCardsRevealKey: (revealKey: string | null) => void;
    setPlayerCardsVisible: (visible: boolean) => void;
}

export class PokerPresentation {
    private readonly boardPresentation: PokerBoardPresentation;
    private readonly audioManager: PokerAudioPresentation;
    private readonly timeline: PokerGameplayPresentation;
    private handledGameplayEventKeys: Set<string> = new Set();
    private handledGameplayEventKeyOrder: string[] = [];

    constructor(private readonly options: PokerPresentationOptions) {
        this.boardPresentation = new PokerBoardPresentation(options.initialCommunityCards);
        this.audioManager = new PokerAudioPresentation({
            audioPresetManager: options.audioPresetManager,
            isPlaybackAllowed: options.isPlaybackAllowed,
            getRoomType: options.getRoomType,
            getCurrentUserId: options.getCurrentUserId
        });
        this.timeline = new PokerGameplayPresentation({
            getRoomIdentity: options.getRoomIdentity,
            setActionBadge: this.applyActionBadgeFromEvent,
            clearActionBadge: options.clearActionBadge,
            clearActionBadges: options.clearActionBadges,
            clearNonTerminalActionBadges: options.clearNonTerminalActionBadges,
            setWinnerIndicators: this.applyWinnerIndicatorsFromEvent,
            clearWinnerIndicators: options.clearWinnerIndicators,
            clearCommunityCards: this.clearCommunityCards,
            clearCompletedHandPresentation: this.clearCompletedHandPresentation,
            revealCommunityCard: this.revealCommunityCard,
            setAnimatedCommunityCardDelaysMs: options.setAnimatedCommunityCardDelaysMs,
            setShowdownCardsRevealKey: options.setShowdownCardsRevealKey,
            setPlayerCardsVisible: options.setPlayerCardsVisible
        }, this.audioManager);
    }

    public getVisibleCommunityCards = (): string[] => {
        return this.boardPresentation.getVisibleCommunityCards();
    };

    public resetVisibleCommunityCardsToSnapshot = (snapshotCards: readonly string[] = []): string[] => {
        return this.boardPresentation.resetToSnapshotCards(snapshotCards);
    };

    public syncVisibleCommunityCardsToSnapshot = (snapshotCards: readonly string[] = []): string[] | null => {
        return this.boardPresentation.syncSnapshot(snapshotCards);
    };

    public handleAcceptedGameplayEvent = (event: IGameplayEvent) => {
        if (event.roomId !== this.options.getRoomId()) return;
        if (!event.eventId || this.handledGameplayEventKeys.has(event.eventId)) return;

        this.rememberGameplayEventKey(event.eventId);
        this.timeline.handleGameplayEvent(event);
    };

    public applyRetainedWinnerDeclaredEvent = (
        event: IGameplayEvent | undefined,
        receivedAt: number | undefined
    ) => {
        if (!event || event.kind !== 'winnerDeclared' || !receivedAt) return;
        if (Date.now() - receivedAt > WINNER_INDICATOR_DISPLAY_MS) return;

        this.handleAcceptedGameplayEvent(event);
    };

    public clearActionBadgeForTurnBoundary = (userId: string | undefined) => {
        this.timeline.clearActionBadgeForTurnBoundary(userId);
    };

    public resetHand = (options: HandResetOptions = {}) => {
        this.timeline.resetHand(options);
    };

    public resetRoom = () => {
        this.timeline.resetRoom();
        this.handledGameplayEventKeys.clear();
        this.handledGameplayEventKeyOrder = [];
    };

    public dispose = () => {
        this.timeline.dispose();
    };

    private rememberGameplayEventKey = (eventId: string) => {
        this.handledGameplayEventKeys.add(eventId);
        this.handledGameplayEventKeyOrder.push(eventId);
        while (this.handledGameplayEventKeyOrder.length > 200) {
            const oldKey = this.handledGameplayEventKeyOrder.shift();
            if (oldKey) this.handledGameplayEventKeys.delete(oldKey);
        }
    };

    private applyActionBadgeFromEvent = (event: IGameplayEvent) => {
        const presentation = createPlayerActionBadgePresentation(event, this.options.getCurrentUserId());
        if (presentation) {
            this.options.applyActionBadge(presentation);
        }
    };

    private applyWinnerIndicatorsFromEvent = (event: IGameplayEvent) => {
        const presentation = createWinnerIndicatorsPresentation(event);
        if (presentation) {
            this.options.applyWinnerIndicators(presentation);
        }
    };

    private revealCommunityCard = (placement: CommunityCardPlacement): boolean => {
        const revealResult = this.boardPresentation.revealCard({
            cardIndex: placement.cardIndex,
            card: placement.card
        });
        if (!revealResult || !revealResult.changed) return false;

        this.options.setVisibleCommunityCards(revealResult.cards);
        return true;
    };

    private clearCommunityCards = () => {
        const visibleCommunityCards = this.boardPresentation.resetToSnapshotCards([]);
        this.options.setVisibleCommunityCards(visibleCommunityCards);
    };

    private clearCompletedHandPresentation = (playerCardsVisibleAfterClear?: boolean) => {
        const visibleCommunityCards = this.boardPresentation.resetToSnapshotCards([]);
        this.options.clearCompletedHandPresentation(visibleCommunityCards, playerCardsVisibleAfterClear);
    };
}
